import { createElement, createContext as createContext$1, useContext, useState, useRef, useEffect, Fragment } from 'react';
import { string, oneOfType, object, array, bool, oneOf, number, func, any } from 'prop-types';
import { ResizeSensor } from 'css-element-queries';

function styleInject(css, ref) {
  if (ref === void 0) ref = {};
  var insertAt = ref.insertAt;

  if (!css || typeof document === 'undefined') {
    return;
  }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css = ".spaces-centered-vertically {\r\n\tposition: relative;\r\n\ttop: 50%;\r\n\ttransform: translateY(-50%);\r\n}\r\n\r\n.spaces-centered {\r\n\tposition: relative;\r\n\ttop: 50%;\r\n\ttransform: translateY(-50%);\r\n\ttext-align: center;\r\n}\r\n\r\n.spaces-clearfix:after {\r\n\tcontent: \"\";\r\n\tdisplay: table;\r\n\tclear: both;\r\n}\r\n\r\n.spaces-fixedsize-layout {\r\n\toverflow: hidden;\r\n\tposition: relative;\r\n}\r\n\r\n.spaces-fullpage-layout {\r\n\tposition: fixed;\r\n\toverflow: hidden;\r\n}\r\n\r\n.spaces-resize-handle {\r\n\tposition: absolute;\r\n\tz-index: 9999;\r\n}\r\n\r\n.spaces-resize-handle.resize-left {\r\n\ttop: 0;\r\n\tbottom: 0;\r\n\tleft: 0;\r\n\tcursor: w-resize;\r\n}\r\n\r\n.spaces-resize-handle.resize-right {\r\n\ttop: 0;\r\n\tbottom: 0;\r\n\tright: 0;\r\n\tcursor: e-resize;\r\n}\r\n\r\n.spaces-resize-handle.resize-top {\r\n\ttop: 0;\r\n\tleft: 0;\r\n\tright: 0;\r\n\tcursor: n-resize;\r\n}\r\n\r\n.spaces-resize-handle.resize-bottom {\r\n\tbottom: 0;\r\n\tleft: 0;\r\n\tright: 0;\r\n\tcursor: s-resize;\r\n}\r\n\r\n.spaces-space {\r\n\tposition: absolute;\r\n\toverflow: hidden;\r\n\tz-index: 1;\r\n}\r\n\r\n.spaces-space .spaces-space-inner {\r\n\tz-index: 1;\r\n\tposition: absolute;\r\n\tleft: 0;\r\n\ttop: 0;\r\n\tright: 0;\r\n\tbottom: 0;\r\n}\r\n\r\n.spaces-space.scrollable .spaces-space-inner {\r\n\toverflow: auto;\r\n}";
styleInject(css);

var Centered = function Centered(props) {
  return createElement("div", {
    className: "spaces-centered"
  }, props.children);
};

var CenteredVertically = function CenteredVertically(props) {
  return createElement("div", {
    className: "spaces-centered-vertically"
  }, props.children);
};

var ClearFix = function ClearFix(props) {
  return createElement("div", {
    className: "spaces-clearfix"
  }, props.children);
};
/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */


var _assign = function __assign() {
  _assign = Object.assign || function __assign(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];

      for (var p in s) {
        if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
      }
    }

    return t;
  };

  return _assign.apply(this, arguments);
};

var ResizeType;

(function (ResizeType) {
  ResizeType["Left"] = "resize-left";
  ResizeType["Right"] = "resize-right";
  ResizeType["Top"] = "resize-top";
  ResizeType["Bottom"] = "resize-bottom";
})(ResizeType || (ResizeType = {}));

var AnchorType;

(function (AnchorType) {
  AnchorType["Left"] = "anchor-left";
  AnchorType["Right"] = "anchor-right";
  AnchorType["Top"] = "anchor-top";
  AnchorType["Bottom"] = "anchor-bottom";
})(AnchorType || (AnchorType = {}));

var AnchorTypes = [AnchorType.Left, AnchorType.Right, AnchorType.Bottom, AnchorType.Top];
var AnchorToResizeTypeMap = {
  "anchor-left": ResizeType.Right,
  "anchor-right": ResizeType.Left,
  "anchor-bottom": ResizeType.Top,
  "anchor-top": ResizeType.Bottom
};
var CenterType;

(function (CenterType) {
  CenterType["None"] = "none";
  CenterType["Vertical"] = "vertical";
  CenterType["HorizontalVertical"] = "horizontalVertical";
})(CenterType || (CenterType = {}));

var publicProps = {
  id: string,
  className: string,
  style: oneOfType([object, array]),
  scrollable: bool,
  trackSize: bool,
  centerContent: oneOf([CenterType.None, CenterType.Vertical, CenterType.HorizontalVertical]),
  as: string,
  debug: bool,
  zIndex: number,
  onClick: func,
  onMouseDown: func,
  onMouseEnter: func,
  onMouseLeave: func
};
var privateProps = {
  anchorSize: oneOfType([string, number]),
  anchor: oneOf([AnchorType.Bottom, AnchorType.Left, AnchorType.Right, AnchorType.Top]),
  resizable: bool,
  order: number
};
var anchoredProps = {
  size: oneOfType([string, number]).isRequired,
  order: number
};
var resizableProps = {
  handleSize: number,
  overlayHandle: bool,
  minimumSize: number,
  maximumSize: number,
  onResizeStart: func,
  onResizeEnd: func
};
var positionedProps = {
  left: oneOfType([string, number]),
  top: oneOfType([string, number]),
  right: oneOfType([string, number]),
  bottom: oneOfType([string, number]),
  width: oneOfType([string, number]),
  height: oneOfType([string, number]),
  resizable: bool
};

var allProps = _assign({}, publicProps, privateProps, resizableProps, positionedProps);

var SpaceContext = createContext$1(null);
var SpaceLayerContext = createContext$1(null);
var SpaceInfoContext = createContext$1(null);

var debounce = function debounce(func, waitFor) {
  var timeout = 0;

  var debounced = function debounced() {
    var args = [];

    for (var _i = 0; _i < arguments.length; _i++) {
      args[_i] = arguments[_i];
    }

    window.clearTimeout(timeout);
    timeout = window.setTimeout(function () {
      return func.apply(void 0, args);
    }, waitFor);
  };

  return debounced;
};

var Resizable = function Resizable(props) {
  var resize = function resize(originalX, originalY, x, y) {
    var adjustmentX = Math.min(Math.max(props.type === ResizeType.Left ? originalX - x : x - originalX, props.minimumAdjust), props.maximumAdjust || 999999);
    var adjustmentY = Math.min(Math.max(props.type === ResizeType.Top ? originalY - y : y - originalY, props.minimumAdjust), props.maximumAdjust || 999999);
    var adjustment = props.type === ResizeType.Left || props.type === ResizeType.Right ? adjustmentX : adjustmentY;

    if (adjustment !== props.adjustedSize) {
      props.onResize(adjustment);
    }
  };

  var startTouchResize = function startTouchResize(e) {
    var originalTouchX = props.type === ResizeType.Left ? e.touches[0].pageX + props.adjustedSize : e.touches[0].pageX - props.adjustedSize;
    var originalTouchY = props.type === ResizeType.Top ? e.touches[0].pageY + props.adjustedSize : e.touches[0].pageY - props.adjustedSize;
    var resizing = true;
    var moved = false;

    var touchResize = function touchResize(e) {
      return resizing && resize(originalTouchX, originalTouchY, e.touches[0].pageX, e.touches[0].pageY);
    };

    var debouncedTouchResize = debounce(touchResize, 10);

    var withPreventDefault = function withPreventDefault(e) {
      moved = true;
      e.preventDefault();
      e.stopImmediatePropagation();
      debouncedTouchResize(e);
    };

    var removeListener = function removeListener() {
      resizing = false;
      window.removeEventListener('touchmove', withPreventDefault);
      window.removeEventListener('touchend', removeListener);
      moved && props.onResizeEnd && props.onResizeEnd();
    };

    window.addEventListener('touchmove', withPreventDefault);
    window.addEventListener('touchend', removeListener);
    e.preventDefault();
    e.stopPropagation();
    props.onResizeStart && props.onResizeStart();
  };

  var startResize = function startResize(e) {
    var originalMouseX = props.type === ResizeType.Left ? e.pageX + props.adjustedSize : e.pageX - props.adjustedSize;
    var originalMouseY = props.type === ResizeType.Top ? e.pageY + props.adjustedSize : e.pageY - props.adjustedSize;
    var resizing = true;
    var moved = false;

    var mouseResize = function mouseResize(e) {
      return resizing && resize(originalMouseX, originalMouseY, e.pageX, e.pageY);
    };

    var debouncedMouseResize = debounce(mouseResize, 10);

    var withPreventDefault = function withPreventDefault(e) {
      moved = true;
      e.preventDefault();
      e.stopImmediatePropagation();
      debouncedMouseResize(e);
    };

    var removeListener = function removeListener() {
      resizing = false;
      window.removeEventListener('mousemove', withPreventDefault);
      window.removeEventListener('mouseup', removeListener);
      moved && props.onResizeEnd && props.onResizeEnd();
    };

    window.addEventListener('mousemove', withPreventDefault);
    window.addEventListener('mouseup', removeListener);
    e.preventDefault();
    e.stopPropagation();
    props.onResizeStart && props.onResizeStart();
  };

  return createElement("div", {
    style: {
      width: props.width,
      height: props.height
    },
    className: "spaces-resize-handle " + props.type,
    onMouseDown: startResize,
    onTouchStart: startTouchResize
  });
};

Resizable.propTypes = {
  type: oneOf([ResizeType.Bottom, ResizeType.Left, ResizeType.Right, ResizeType.Top]).isRequired,
  width: number,
  height: number,
  minimumAdjust: number.isRequired,
  maximumAdjust: number,
  onResize: any
};

var getSizeString = function getSizeString(size) {
  return typeof size === "string" ? size : size + "px";
};

var isFilledSpace = function isFilledSpace(props) {
  return !props.anchor;
};

var isHorizontalSpace = function isHorizontalSpace(props) {
  return props.anchor && (props.anchor === AnchorType.Left || props.anchor === AnchorType.Right);
};

var isVerticalSpace = function isVerticalSpace(props) {
  return props.anchor && (props.anchor === AnchorType.Top || props.anchor === AnchorType.Bottom);
};

var uuid = function uuid() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (char) {
    var random = Math.random() * 16 | 0;
    var value = char === "x" ? random : random % 4 + 8;
    return value.toString(16);
  });
};

var initialState = function initialState(props) {
  return {
    id: props.id || uuid(),
    currentWidth: 0,
    currentHeight: 0,
    adjustedSize: 0,
    adjustedLeft: 0,
    adjustedTop: 0,
    spaceTakers: [],
    parsedSize: typeof props.anchorSize === "string" ? 0 : props.anchorSize,
    left: props.anchor && props.anchor !== AnchorType.Right ? props.left || 0 : props.left,
    top: props.anchor && props.anchor !== AnchorType.Bottom ? props.top || 0 : props.top,
    right: props.anchor && props.anchor !== AnchorType.Left ? props.right || 0 : props.right,
    bottom: props.anchor && props.anchor !== AnchorType.Top ? props.bottom || 0 : props.bottom,
    width: props.anchor && isHorizontalSpace(props) ? props.anchorSize || 0 : props.width,
    height: props.anchor && isVerticalSpace(props) ? props.anchorSize || 0 : props.height,
    debug: props.debug !== undefined ? props.debug : false
  };
};

var createContext = function createContext(state, setState, parent, zIndex) {
  var context = {
    debug: parent ? parent.debug ? true : state.debug : state.debug,
    level: parent ? parent.level + 1 : 0,
    zIndex: zIndex,
    width: state.currentWidth,
    height: state.currentHeight,
    spaceTakers: state.spaceTakers,
    registerSpaceTaker: function registerSpaceTaker(spaceTaker) {
      var existing = state.spaceTakers.find(function (t) {
        return t.id === spaceTaker.id;
      });

      if (!existing) {
        setState({
          spaceTakers: state.spaceTakers.concat([spaceTaker])
        });
      } else {
        existing.order = spaceTaker.order;
        existing.anchorType = spaceTaker.anchorType;
        existing.size = spaceTaker.size;
      }
    },
    removeSpaceTaker: function removeSpaceTaker(id) {
      return setState({
        spaceTakers: state.spaceTakers.filter(function (t) {
          return t.id !== id;
        })
      });
    },
    updateSpaceTakerAdjustedSize: function updateSpaceTakerAdjustedSize(id, adjustedSize) {
      return setState({
        spaceTakers: state.spaceTakers.map(function (t) {
          return t.id === id ? _assign({}, t, {
            adjustedSize: adjustedSize
          }) : t;
        })
      });
    },
    updateSpaceTakerLayer: function updateSpaceTakerLayer(id, zIndex) {
      return setState({
        spaceTakers: state.spaceTakers.map(function (t) {
          return t.id === id ? _assign({}, t, {
            zIndex: zIndex
          }) : t;
        })
      });
    },
    updateDebug: function updateDebug(id, debug) {
      return setState({
        spaceTakers: state.spaceTakers.map(function (t) {
          return t.id === id ? _assign({}, t, {
            debug: debug
          }) : t;
        })
      });
    },
    startDrag: function startDrag(e) {
      var originalMouseX = e.pageX - state.adjustedLeft;
      var originalMouseY = e.pageY - state.adjustedTop;

      var drag = function drag(e) {
        return setState({
          adjustedLeft: e.pageX - originalMouseX,
          adjustedTop: e.pageY - originalMouseY
        });
      };

      var stopDrag = function stopDrag() {
        return window.removeEventListener('mousemove', drag);
      };

      e.preventDefault();
      window.addEventListener('mousemove', drag);
      window.addEventListener('mouseup', stopDrag);
    }
  };
  return context;
};

var applyResize = function applyResize(props, state, setState, parentContext, handleSize, divElementRef) {
  if (parentContext && props.anchor && props.resizable) {
    var resizeType_1 = AnchorToResizeTypeMap[props.anchor];
    var resizeHandleWidth = resizeType_1 === ResizeType.Left || resizeType_1 === ResizeType.Right ? props.handleSize || 5 : undefined;
    var resizeHandleHeight = resizeType_1 === ResizeType.Top || resizeType_1 === ResizeType.Bottom ? props.handleSize || 5 : undefined;
    return {
      resizeHandle: createElement(Resizable, {
        type: resizeType_1,
        adjustedSize: state.adjustedSize,
        width: resizeHandleWidth,
        height: resizeHandleHeight,
        minimumAdjust: (props.minimumSize || 20) - (state.parsedSize || 0),
        maximumAdjust: props.maximumSize ? props.maximumSize - (state.parsedSize || 0) : undefined,
        onResize: function onResize(adjustedSize) {
          setState({
            adjustedSize: adjustedSize
          });
          parentContext.updateSpaceTakerAdjustedSize(state.id, adjustedSize);
        },
        onResizeStart: function onResizeStart() {
          return props.onResizeStart && props.onResizeStart();
        },
        onResizeEnd: function onResizeEnd() {
          if (divElementRef.current) {
            var currentRect = divElementRef.current.getBoundingClientRect();
            props.onResizeEnd && props.onResizeEnd(resizeType_1 === ResizeType.Left || resizeType_1 === ResizeType.Right ? currentRect.width : currentRect.height);
          }
        }
      }),
      resizeType: resizeType_1
    };
  }

  return {
    resizeHandle: null,
    resizeType: null
  };
};

var useSpace = function useSpace(props, divElementRef) {
  var _a = useState(initialState(props)),
      state = _a[0],
      changeState = _a[1];

  var resizeSensor = useRef();
  var onRemove = useRef();

  var setState = function setState(stateDelta) {
    return changeState(function (prev) {
      return _assign({}, prev, stateDelta);
    });
  };

  var parentContext = useContext(SpaceContext);
  var layerContext = useContext(SpaceLayerContext);
  var currentZIndex = props.zIndex || layerContext || 0;
  var previouszIndex = usePrevious(currentZIndex); // Deal with property changes to size / anchoring 

  useEffect(function () {
    setState({
      parsedSize: typeof props.anchorSize === "string" ? 0 : props.anchorSize,
      left: props.anchor !== AnchorType.Right ? props.left || 0 : undefined,
      top: props.anchor !== AnchorType.Bottom ? props.top || 0 : undefined,
      right: props.anchor !== AnchorType.Left ? props.right || 0 : undefined,
      bottom: props.anchor !== AnchorType.Top ? props.bottom || 0 : undefined,
      width: isHorizontalSpace(props) ? props.anchorSize || 0 : props.width,
      height: isVerticalSpace(props) ? props.anchorSize || 0 : props.height,
      debug: props.debug !== undefined ? props.debug : false,
      adjustedSize: 0
    });

    if (parentContext) {
      parentContext.updateSpaceTakerAdjustedSize(state.id, 0);

      if (currentZIndex !== previouszIndex) {
        parentContext.updateSpaceTakerLayer(state.id, currentZIndex);
      }
    }
  }, [currentZIndex, props.left, props.top, props.bottom, props.right, props.width, props.height, props.anchor, props.anchorSize, props.debug]); // Setup / cleanup

  useEffect(function () {
    if (divElementRef.current) {
      if (props.trackSize) {
        resizeSensor.current = new ResizeSensor(divElementRef.current, function (size) {
          return setState({
            currentWidth: size.width,
            currentHeight: size.height
          });
        });
      }
    }

    var cleanup = function cleanup() {
      resizeSensor.current && resizeSensor.current.detach();
      onRemove.current && onRemove.current();
    };

    return cleanup;
  }, []);
  useEffect(function () {
    if (divElementRef.current && !state.parsedSize) {
      var currentRect = divElementRef.current.getBoundingClientRect();
      setState({
        parsedSize: isHorizontalSpace(props) ? currentRect.width : currentRect.height,
        currentWidth: currentRect.width,
        currentHeight: currentRect.height
      });
    }
  });
  var outerStyle = {
    left: state.left !== undefined ? "calc(" + getSizeString(state.left || 0) + (state.adjustedLeft ? " + " + state.adjustedLeft + "px" : "") + ")" : undefined,
    top: state.top !== undefined ? "calc(" + getSizeString(state.top || 0) + (state.adjustedTop ? " + " + state.adjustedTop + "px" : "") + ")" : undefined,
    right: state.right !== undefined ? "calc(" + getSizeString(state.right || 0) + (state.adjustedLeft ? " - " + state.adjustedLeft + "px" : "") + ")" : undefined,
    bottom: state.bottom !== undefined ? "calc(" + getSizeString(state.bottom || 0) + (state.adjustedTop ? " - " + state.adjustedTop + "px" : "") + ")" : undefined,
    width: isHorizontalSpace(props) ? "calc(" + getSizeString(props.anchorSize || 0) + " + " + state.adjustedSize + "px)" : state.width,
    height: isVerticalSpace(props) ? "calc(" + getSizeString(props.anchorSize || 0) + " + " + state.adjustedSize + "px)" : state.height,
    zIndex: currentZIndex
  };

  if (parentContext) {
    onRemove.current = function () {
      parentContext.removeSpaceTaker(state.id);
    };

    var adjustedTop = [];
    var adjustedLeft = [];
    var adjustedRight = [];
    var adjustedBottom = [];

    var _loop_1 = function _loop_1(at) {
      var spaceTakers = parentContext.spaceTakers.filter(function (t) {
        return t.zIndex === currentZIndex && t.anchorType === AnchorTypes[at];
      }).sort(function (a, b) {
        return a.order - b.order;
      });

      for (var i = 0; i < spaceTakers.length; i++) {
        var t = spaceTakers[i];

        if (t.id !== state.id) {
          var adjustedSize = t.adjustedSize !== 0 ? " + " + t.adjustedSize + "px" : "";

          if (isFilledSpace(props)) {
            if (t.anchorType === AnchorType.Top) {
              adjustedTop.push("" + getSizeString(t.size) + adjustedSize);
            } else if (t.anchorType === AnchorType.Left) {
              adjustedLeft.push("" + getSizeString(t.size) + adjustedSize);
            } else if (t.anchorType === AnchorType.Bottom) {
              adjustedBottom.push("" + getSizeString(t.size) + adjustedSize);
            } else if (t.anchorType === AnchorType.Right) {
              adjustedRight.push("" + getSizeString(t.size) + adjustedSize);
            }
          } else {
            if (t.anchorType === AnchorType.Top && outerStyle.top !== undefined) {
              adjustedTop.push("" + getSizeString(t.size) + adjustedSize);
            } else if (t.anchorType === AnchorType.Left && outerStyle.left !== undefined) {
              adjustedLeft.push("" + getSizeString(t.size) + adjustedSize);
            } else if (t.anchorType === AnchorType.Bottom && outerStyle.bottom !== undefined) {
              adjustedBottom.push("" + getSizeString(t.size) + adjustedSize);
            } else if (t.anchorType === AnchorType.Right && outerStyle.right !== undefined) {
              adjustedRight.push("" + getSizeString(t.size) + adjustedSize);
            }
          }
        } else {
          break;
        }
      }
    };

    for (var at = 0; at < AnchorTypes.length; at++) {
      _loop_1(at);
    }

    [{
      adjusted: adjustedTop,
      setter: function setter(value) {
        return outerStyle.top = value;
      }
    }, {
      adjusted: adjustedBottom,
      setter: function setter(value) {
        return outerStyle.bottom = value;
      }
    }, {
      adjusted: adjustedLeft,
      setter: function setter(value) {
        return outerStyle.left = value;
      }
    }, {
      adjusted: adjustedRight,
      setter: function setter(value) {
        return outerStyle.right = value;
      }
    }].map(function (x) {
      return x.adjusted.length > 0 && x.setter("calc(" + x.adjusted.join(" + ") + ")");
    });
    props.anchor && parentContext.registerSpaceTaker({
      id: state.id,
      zIndex: currentZIndex,
      order: props.order || 1,
      anchorType: props.anchor,
      size: props.anchorSize || 0,
      adjustedSize: 0
    });
  }

  var currentContext = createContext(state, setState, parentContext, currentZIndex);
  var handleSize = props.handleSize || 5;
  var overlayHandle = props.overlayHandle !== undefined ? props.overlayHandle : true;
  var resize = applyResize(props, state, setState, parentContext, handleSize, divElementRef);

  var innerStyle = _assign({}, props.style, {
    left: resize.resizeType === ResizeType.Left && !overlayHandle ? handleSize : undefined,
    top: resize.resizeType === ResizeType.Top && !overlayHandle ? handleSize : undefined,
    right: resize.resizeType === ResizeType.Right && !overlayHandle ? handleSize : undefined,
    bottom: resize.resizeType === ResizeType.Bottom && !overlayHandle ? handleSize : undefined
  });

  var debug = parentContext ? parentContext.debug : props.debug !== undefined ? props.debug : false;
  var userClasses = props.className ? props.className.split(' ').map(function (c) {
    return c.trim();
  }) : [];
  var outerClasses = ["spaces-space", props.anchor || undefined, resize.resizeType || undefined, props.scrollable ? "scrollable" : undefined, debug ? 'debug' : undefined].concat(userClasses.map(function (c) {
    return c + "-container";
  })).filter(function (c) {
    return c;
  });
  var innerClasses = ["spaces-space-inner"].concat(userClasses).filter(function (c) {
    return c;
  });
  return {
    currentContext: currentContext,
    resizeHandle: resize.resizeHandle,
    outerStyle: outerStyle,
    outerClasses: outerClasses,
    innerStyle: innerStyle,
    innerClasses: innerClasses,
    currentWidth: state.currentWidth,
    currentHeight: state.currentHeight
  };
};

function usePrevious(value) {
  // The ref object is a generic container whose current property is mutable ...
  // ... and can hold any value, similar to an instance property on a class
  var ref = useRef(); // Store current value in ref

  useEffect(function () {
    ref.current = value;
  }, [value]); // Only re-run if value changes
  // Return previous value (happens before update in useEffect above)

  return ref.current;
}

var useParentSpace = function useParentSpace() {
  var parentContext = useContext(SpaceContext);
  return {
    startDrag: parentContext ? parentContext.startDrag : function () {
      return null;
    }
  };
};

var Fill = function Fill(props) {
  return createElement(SpaceInternal, _assign({}, props));
};

Fill.propTypes = publicProps;

var Top = function Top(props) {
  return createElement(SpaceInternal, _assign({}, props, {
    anchor: AnchorType.Top,
    anchorSize: props.size
  }));
};

Top.propTypes = _assign({}, publicProps, anchoredProps);

var Left = function Left(props) {
  return createElement(SpaceInternal, _assign({}, props, {
    anchor: AnchorType.Left,
    anchorSize: props.size
  }));
};

Left.propTypes = _assign({}, publicProps, anchoredProps);

var Bottom = function Bottom(props) {
  return createElement(SpaceInternal, _assign({}, props, {
    anchor: AnchorType.Bottom,
    anchorSize: props.size
  }));
};

Bottom.propTypes = _assign({}, publicProps, anchoredProps);

var Right = function Right(props) {
  return createElement(SpaceInternal, _assign({}, props, {
    anchor: AnchorType.Right,
    anchorSize: props.size
  }));
};

Right.propTypes = _assign({}, publicProps, anchoredProps);

var TopResizable = function TopResizable(props) {
  return createElement(SpaceInternal, _assign({}, props, {
    anchor: AnchorType.Top,
    anchorSize: props.size,
    resizable: true
  }));
};

TopResizable.propTypes = _assign({}, publicProps, anchoredProps, resizableProps);

var LeftResizable = function LeftResizable(props) {
  return createElement(SpaceInternal, _assign({}, props, {
    anchor: AnchorType.Left,
    anchorSize: props.size,
    resizable: true
  }));
};

LeftResizable.propTypes = _assign({}, publicProps, anchoredProps, resizableProps);

var BottomResizable = function BottomResizable(props) {
  return createElement(SpaceInternal, _assign({}, props, {
    anchor: AnchorType.Bottom,
    anchorSize: props.size,
    resizable: true
  }));
};

BottomResizable.propTypes = _assign({}, publicProps, anchoredProps, resizableProps);

var RightResizable = function RightResizable(props) {
  return createElement(SpaceInternal, _assign({}, props, {
    anchor: AnchorType.Right,
    anchorSize: props.size,
    resizable: true
  }));
};

RightResizable.propTypes = _assign({}, publicProps, anchoredProps, resizableProps);

var Positioned = function Positioned(props) {
  return createElement(SpaceInternal, _assign({}, props));
};

RightResizable.propTypes = _assign({}, publicProps, resizableProps, positionedProps);

var SpaceInternal = function SpaceInternal(props) {
  var divElementRef = useRef();

  var _a = useSpace(props, divElementRef),
      currentContext = _a.currentContext,
      outerStyle = _a.outerStyle,
      outerClasses = _a.outerClasses,
      innerStyle = _a.innerStyle,
      innerClasses = _a.innerClasses,
      resizeHandle = _a.resizeHandle,
      currentWidth = _a.currentWidth,
      currentHeight = _a.currentHeight;

  var children = props.children;

  if (props.centerContent === CenterType.Vertical) {
    children = createElement(CenteredVertically, null, children);
  } else if (props.centerContent === CenterType.HorizontalVertical) {
    children = createElement(Centered, null, children);
  }

  return createElement(SpaceContext.Provider, {
    value: currentContext
  }, createElement(SpaceInfoContext.Provider, {
    value: {
      width: Math.floor(currentWidth),
      height: Math.floor(currentHeight)
    }
  }, createElement(props.as || 'div', {
    id: props.id,
    ref: divElementRef,
    className: outerClasses.join(' '),
    style: outerStyle,
    onClick: props.onClick,
    onMouseDown: props.onMouseDown,
    onMouseEnter: props.onMouseEnter,
    onMouseLeave: props.onMouseLeave
  }, createElement(Fragment, null, resizeHandle, createElement("div", {
    className: innerClasses.join(' '),
    style: innerStyle
  }, children)))));
};

SpaceInternal.propTypes = allProps;

var Fixed = function Fixed(props) {
  var style = _assign({}, props.style, {
    width: props.width,
    height: props.height
  });

  return createElement("div", {
    className: "spaces-fixedsize-layout" + (props.className ? " " + props.className : ""),
    style: style
  }, createElement(Fill, null, props.children));
};

Fixed.propTypes = {
  className: string,
  style: oneOfType([object, array]),
  width: number,
  height: number.isRequired
};

var ViewPort = function ViewPort(props) {
  return createElement("div", {
    className: "spaces-fullpage-layout" + (props.className ? " " + props.className : ""),
    style: {
      left: props.left || 0,
      top: props.top || 0,
      right: props.right || 0,
      bottom: props.bottom || 0
    }
  }, createElement(Fill, null, props.children));
};

ViewPort.propTypes = {
  className: string,
  left: number,
  top: number,
  right: number,
  bottom: number
};

var Layer = function Layer(props) {
  return createElement(SpaceLayerContext.Provider, {
    value: props.zIndex
  }, props.children);
};

var Info = function Info(props) {
  return createElement(SpaceInfoContext.Consumer, null, function (info) {
    return props.children(info);
  });
};

export { AnchorType, Bottom, BottomResizable, CenterType, Centered, CenteredVertically, ClearFix, Fill, Fixed, Info, Layer, Left, LeftResizable, Positioned, Right, RightResizable, Top, TopResizable, ViewPort, useParentSpace };